import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, GridLegacy as Grid, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DesktopDateTimePicker } from '@mui/x-date-pickers-pro';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { getNotifications } from '~/services/SailthruService';
import { Dayjs } from '~/services/TimeService';

import getColumns from './getColumns';

const NOTIFICATION_TYPE = 'business_employee_invited';
const BRAND = 'lebusinesstraveller';

interface FormValues {
  lastOccurredAtStart: Dayjs | null;
  lastOccurredAtEnd: Dayjs | null;
}

function EmployeeActivationEmailContainer() {
  const { enqueueSnackbar } = useSnackbar();
  const [pageNum, setPageNum] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchFilters, setSearchFilters] = useState<{
    lastOccurredAtStart: Date | null;
    lastOccurredAtEnd: Date | null;
  }>({
    lastOccurredAtStart: null,
    lastOccurredAtEnd: null,
  });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      lastOccurredAtStart: null,
      lastOccurredAtEnd: null,
    },
  });

  const {
    data: queryData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'lebt-activation-emails',
      pageNum,
      pageSize,
      searchFilters.lastOccurredAtStart?.toISOString(),
      searchFilters.lastOccurredAtEnd?.toISOString(),
    ],
    queryFn: async () => {
      const result = await getNotifications({
        notificationType: NOTIFICATION_TYPE,
        brand: BRAND,
        pageNum,
        pageSize,
        lastOccurredAtStart: searchFilters.lastOccurredAtStart,
        lastOccurredAtEnd: searchFilters.lastOccurredAtEnd,
      });
      return result;
    },
    placeholderData: (previousData) => previousData,
  });

  const data = queryData?.result?.data ?? [];
  const totalCount = Number(queryData?.result?.total ?? 0);

  const dataRows = data.map((row) => ({
    id: row.notification_id,
    ...row,
  }));

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Failed to fetch activation emails', { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const onPageChange = useCallback((page: number, newPageSize: number) => {
    setPageNum(page);
    setPageSize(newPageSize);
  }, []);

  const handleSearch = useCallback((formValues: FormValues) => {
    setSearchFilters({
      lastOccurredAtStart: formValues.lastOccurredAtStart?.toDate() ?? null,
      lastOccurredAtEnd: formValues.lastOccurredAtEnd?.toDate() ?? null,
    });
    setPageNum(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    reset({
      lastOccurredAtStart: null,
      lastOccurredAtEnd: null,
    });
  }, [reset]);

  return (
    <>
      <PageSubheader title="Activation Emails" />

      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
        component="form"
        onSubmit={handleSubmit(handleSearch)}
      >
        <Grid item>
          <Controller
            control={control}
            name="lastOccurredAtStart"
            render={({ field }) => {
              return (
                <DesktopDateTimePicker
                  label="From Date (UTC)"
                  disableFuture
                  timezone="UTC"
                  sx={{ width: 300 }}
                  ampm={false}
                  onChange={field.onChange}
                  onAccept={field.onChange}
                  value={field.value}
                  inputRef={field.ref}
                />
              );
            }}
          />
        </Grid>
        <Grid item>
          <Controller
            control={control}
            name="lastOccurredAtEnd"
            render={({ field }) => {
              return (
                <DesktopDateTimePicker
                  label="To Date (UTC)"
                  disableFuture
                  timezone="UTC"
                  sx={{ width: 300 }}
                  ampm={false}
                  onChange={field.onChange}
                  onAccept={field.onChange}
                  value={field.value}
                  inputRef={field.ref}
                />
              );
            }}
          />
        </Grid>

        <Grid item>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" type="submit">
              Search
            </Button>
            <Button variant="text" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={dataRows}
          columns={getColumns()}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize }}
        />
      </Box>
    </>
  );
}

export default EmployeeActivationEmailContainer;
